import { Card, Row, Col, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import dayjs from 'dayjs';
import RequiredFormLabel from '../../../components/RequiredFormLabel/RequiredFormLabel';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { FORMAT_DATE, FORMAT_DATE_TIME } from '../../../utils/constants';
import { renderFloatAsStr } from '../../../utils';
import { useAuth } from '../../../hooks/useAuth';
import { RoleEnum } from '../../../api';
import TiassistoElement from '../../TiAssisto/TiassistoElement';
import {
  DriveItEasyStatusEnum,
  OpportunityStageEnum,
} from '../../../api/models/Enums';
import { useCallback, useMemo } from 'react';
import { useEffect, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import _ from 'lodash';
import { isMarket } from 'src/utils/markets';
import { Markets, RemoteSelectOption } from 'src/types';
import { FaInfoCircle } from 'react-icons/fa';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { useErrorToast } from 'src/hooks/useErrorToast';

const OpportunityDetails = ({
  values,
  handleChange,
  setFieldValue,
  errors,
  opportunityFields,
  opportunityResponse,
  driveItEasyStatus,
  accountResponse,
  opportunityBackofficeData,
  onProvvigioneChange,
  loadingOpportunitySalesUser,
  opportunitySalesUser,
  isOwnerHmpSalesInternal,
  isOwnerHmpSalesDealer,
  isOpportunityReadOnly,
}) => {
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const [salesUsers, setSalesUsers] = useState([]);
  const [referenceUsers, setReferenceUsers] = useState<RemoteSelectOption[]>(
    []
  );
  const [backupUsers, setBackupUsers] = useState([]);
  const { apiClient, handleApiResponse } = useApiClient();
  const { showError } = useErrorToast();

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getSalesUsers())
      .then((res) => {
        setSalesUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getReferenceUsers())
      .then((res) => {
        const items: RemoteSelectOption[] = res.map((item) =>
          _.isString(item)
            ? {
              value: item,
              label: item,
            }
            : item
        );
        setReferenceUsers(items);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getBackupUsers())
      .then((res) => {
        setBackupUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  function isButtonDisabled(userRole) {
    if (driveItEasyStatus === DriveItEasyStatusEnum.ATTIVO) {
      return true;
    }

    return ![
      RoleEnum.SALES,
      RoleEnum.SALES_DEALER,
      RoleEnum.SALES_INTERNAL,
      RoleEnum.MASTER,
      RoleEnum.BACKOFFICE,
      RoleEnum.AREA_MANAGER,
      RoleEnum.SEGNALATORE,
    ].includes(userRole);
  }

  const isOpportunityFieldRequired = useCallback(
    (field) => {
      if (field.name == 'LeadSource') {
        return false;
      }

      if (field.name !== 'Offerta_Edison__c') {
        return undefined;
      }

      if (isMarket(Markets.es)) {
        return undefined;
      }

      return opportunityResponse.StageName === OpportunityStageEnum.closed_won;
    },
    [opportunityResponse]
  );

  const referenceUsersOptions = useMemo(() => {
    const userId = values?.referenceUserId;
    if (!userId) return referenceUsers;
    if (!referenceUsers) return [];

    const found = (referenceUsers || []).find((item) => item.value === userId);
    if (found) return referenceUsers;

    let label =
      opportunityBackofficeData?.referenceUser ||
      (opportunityResponse?.Owner?.Id === userId
        ? opportunityResponse?.Owner?.Email
        : undefined);

    return [
      ...referenceUsers,
      {
        value: userId,
        label,
        disabled: false,
      },
    ];
  }, [
    values?.referenceUserId,
    referenceUsers,
    opportunityBackofficeData?.referenceUser,
    opportunityResponse?.Owner?.Id,
    opportunityResponse?.Owner?.Email,
  ]);

  const salesUsersOptions = useMemo(() => {
    if (!values?.salesUser) {
      return salesUsers || [];
    }

    if (!salesUsers) return [];

    const found = (salesUsers || []).find((item) => item === values.salesUser);
    if (found) return salesUsers;

    return [...salesUsers, values.salesUser];
  }, [salesUsers, values.salesUser]);

  const isSegnalazione = opportunityResponse?.Canale__c?.toLowerCase() === 'segnalatore';
  const showProvvigioniSegnalatore = [RoleEnum.SALES_INTERNAL, RoleEnum.SALES_DEALER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER, RoleEnum.MASTER, RoleEnum.SEGNALATORE].includes(userRole) && isSegnalazione;
  const isBam = [RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER, RoleEnum.MASTER].includes(userRole);
  const isSegnalatore = userRole === RoleEnum.SEGNALATORE;

  const hasProvvValue = (value: any) => {
    if (_.isNil(value) || _.isEmpty(value)) return false;
    const parsedValue = _.isNumber(value) ? value : parseFloat(value);
    return parsedValue > 0;
  }

  const isAnySales = [RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole);
  const hasSalesDealerWarning = isOwnerHmpSalesDealer && hasProvvValue(opportunityResponse?.Provvigioni_canale__c);
  const hasSalesInternalWarning = isOwnerHmpSalesInternal && hasProvvValue(opportunityResponse?.Provvigioni_passive__c);
  const showCommissionsWarning = isAnySales && (hasSalesDealerWarning || hasSalesInternalWarning);

  useEffect(() => {
    console.debug('[Opportunity.provvigioni]', {
      userRole,
      isOwnerHmpSalesDealer,
      isOwnerHmpSalesInternal,
      opportunityInfo: _.pick(opportunityResponse, ['Provvigioni_canale__c', 'Provvigioni_passive__c', 'Provvigioni__c', 'Provvigioni_Segnalatore__c', 'Canale__c', 'Segnalatore_HMP__c', 'Owner_HMP__c']),
      opportunitySalesUser: _.pick(opportunitySalesUser, ['role', 'username'])
    })
  }, [isOwnerHmpSalesDealer, isOwnerHmpSalesInternal, opportunityResponse]);

  return (
    <Card className="mb-4">
      <fieldset disabled={isOpportunityReadOnly}>
        {showCommissionsWarning && <Card.Header className="m-0 py">
          <Alert variant="warning" className="m-0">
            {i18next.t('opportunity_commissions_warning')}
          </Alert>
        </Card.Header>}
        <Card.Body>
          <div>
            <Row>
              {/* NAME */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('name')} />
                  <Form.Control
                    type="text"
                    name="name"
                    required
                    value={values.name}
                    onChange={({ target }) => setFieldValue(target.name, `HMP-${target.value.replace(/^HMP-?/, '')}`)}
                  />
                </Form.Group>
              </Col>

              {/* DATE CREATION */}
              <Col md={4}>
                <RequiredFormLabel label={i18next.t('creation_date')} required={false} />
                <Form.Control
                  type="text"
                  disabled
                  value={dayjs(opportunityResponse.CreatedDate).format(FORMAT_DATE_TIME)}
                />
              </Col>

              {/* DRIVE IT EASY */}
              <TiassistoElement>
                <Col md={2}>
                  <Form.Group className="mb-3 d-flex flex-column align-items-center">
                    <Form.Label className="me-3" htmlFor="driveItEasyAnchor">
                      {t('drive_it_easy')}
                    </Form.Label>
                    <Form.Check
                      id="driveItEasyAnchor"
                      name="driveItEasy"
                      type="switch"
                      onChange={handleChange}
                      checked={values.driveItEasy}
                      className="hor-form-check-switch mt-2"
                      disabled={isButtonDisabled(userRole)}
                    />
                  </Form.Group>
                </Col>
              </TiassistoElement>
            </Row>

            <Row className="mt-2">
              {/* DATA INVIO AL CREDITO */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('credit_score_check_date')} required={false} />
                  <Form.Control
                    type="text"
                    disabled
                    value={
                      opportunityResponse.Data_invio_al_credito__c
                        ? dayjs(opportunityResponse.Data_invio_al_credito__c).format(FORMAT_DATE)
                        : ''
                    }
                  />
                </Form.Group>
              </Col>
              {/* COMMISSIONS */}
              {isBam && (
                <>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {i18next.t('provvigioni_canale')}
                        <span className="red-ast">*</span>
                        <small id="info-commissions-euro" className='text-muted ms-2'>
                          <FaInfoCircle size={18} />
                          <ToolTip
                            anchorId="info-commissions-euro"
                            content={t('info_commissions_euro')}
                            className="default"
                          />
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="commissions"
                        required
                        value={values.commissions}
                        onChange={({ target }) => {
                          const parsedValue = renderFloatAsStr(target.value);
                          setFieldValue(target.name, parsedValue)
                          onProvvigioneChange("commissions", parsedValue, values, setFieldValue);
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {i18next.t('provvigioni_passive')}
                        <span className="red-ast">*</span>
                        <small id="info-commissions-euro" className='text-muted ms-2'>
                          <FaInfoCircle size={18} />
                          <ToolTip
                            anchorId="info-commissions-euro"
                            content={t('info_commissions_euro')}
                            className="default"
                          />
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="provvigioniPassive"
                        required
                        value={values.provvigioniPassive}
                        onChange={({ target }) => {
                          const parsedValue = renderFloatAsStr(target.value);
                          setFieldValue(target.name, parsedValue)
                          onProvvigioneChange("provvigioniPassive", parsedValue, values, setFieldValue);
                        }}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              {userRole === RoleEnum.SALES_INTERNAL && (
                <>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {i18next.t('provvigioni_canale')}
                        <span className="red-ast">*</span>
                        <small id="info-commissions-euro" className='text-muted ms-2'>
                          <FaInfoCircle size={18} />
                          <ToolTip
                            anchorId="info-commissions-euro"
                            content={t('info_commissions_euro')}
                            className="default"
                          />
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="commissions"
                        required
                        value={values.commissions}
                        onChange={({ target }) => {
                          const parsedValue = renderFloatAsStr(target.value);
                          setFieldValue(target.name, parsedValue)
                          onProvvigioneChange("commissions", parsedValue, values, setFieldValue);
                        }}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              {userRole === RoleEnum.SALES_DEALER && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {i18next.t('provvigioni_passive')}
                      <span className="red-ast">*</span>
                      <small id="info-commissions-euro" className='text-muted ms-2'>
                        <FaInfoCircle size={18} />
                        <ToolTip
                          anchorId="info-commissions-euro"
                          content={t('info_commissions_euro')}
                          className="default"
                        />
                      </small>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="provvigioniPassive"
                      required
                      value={values.provvigioniPassive}
                      onChange={({ target }) => {
                        const parsedValue = renderFloatAsStr(target.value);
                        setFieldValue(target.name, parsedValue)
                        onProvvigioneChange("provvigioniPassive", parsedValue, values, setFieldValue);
                      }}
                    />
                  </Form.Group>
                </Col>
              )}
              {showProvvigioniSegnalatore && !loadingOpportunitySalesUser && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {i18next.t('provvigioni_segnalatore')}
                      <span className="red-ast">*</span>
                      <small id="segn-info-commissions-euro" className='text-muted ms-2'>
                        <FaInfoCircle size={18} />
                        <ToolTip
                          anchorId="segn-info-commissions-euro"
                          content={t('info_commissions_euro')}
                          className="default"
                        />
                      </small>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="provvigioniSegnalatore"
                      required
                      disabled={isSegnalatore}
                      value={values.provvigioniSegnalatore}
                      onChange={({ target }) => {
                        const parsedValue = renderFloatAsStr(target.value);
                        setFieldValue(target.name, parsedValue)
                        onProvvigioneChange("provvigioniSegnalatore", parsedValue, values, setFieldValue);
                      }}
                    />
                  </Form.Group>
                </Col>
              )}
              {/* These fields come from SF */}
              {/* Tipologia, Noleggiatore, Canale, Offerta Edison */}
              {opportunityFields.map((el) => {
                if (el.name === 'LeadSource' && userRole === RoleEnum.SEGNALATORE) {
                  return null;
                }
                return (
                  <Col md={6} key={el.name}>
                    <Form.Group className="mb-3">
                      <RequiredFormLabel label={el.label} required={isOpportunityFieldRequired(el)} />
                      <Select
                        menuPosition="fixed"
                        isDisabled={['canale__c', 'leadsource', 'noleggiatore__c'].includes(el.name?.toLowerCase()) || isOpportunityReadOnly}
                        name={el.name}
                        isClearable={['Offerta_Edison__c'].includes(el.name)}
                        placeholder={['leadsource'].includes(el.name?.toLowerCase()) ? '' : `${i18next.t('select')} ${el.label.toLowerCase()}`}
                        options={el.picklistValues}
                        value={values[el.name]}
                        onChange={(opt) => {
                          setFieldValue(el.name, opt);
                        }}
                      />
                    </Form.Group>
                  </Col>
                )
              })}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('sales_user')} />
                  <Form.Select
                    aria-label="Sales User"
                    name='salesUser'
                    value={values.salesUser}
                    disabled={!!values.salesUser || isOpportunityReadOnly}
                    onChange={handleChange}
                  >
                    <option value=''>{i18next.t('select_sales_user')}</option>
                    {salesUsersOptions.map((user: any) => (
                      <option key={user} value={user}>
                        {user}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('reference_user')} />
                  <Form.Select
                    aria-label="Reference User"
                    disabled={!!values.referenceUserId || isOpportunityReadOnly}
                    name='referenceUserId'
                    value={values.referenceUserId}
                    onChange={handleChange}
                  >
                    <option value=''>{i18next.t('select_reference_user')}</option>
                    {referenceUsersOptions.map((user: any) => (
                      <option key={user.value} value={user.value} disabled={user.disabled}>
                        {user.label} {user.disabled ? `(${user.disabledReason})` : null}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('backup_users')} required={false} />
                  <Select
                    menuPosition="fixed"
                    aria-label="Backup Users"
                    name='backupUsers'
                    isDisabled={isOpportunityReadOnly}
                    noOptionsMessage={() => t('no_options')}
                    value={values.backupUsers ? values.backupUsers.map((user: string) => ({ label: user, value: user })) : []}
                    onChange={
                      (opts: any) => setFieldValue('backupUsers', opts ? opts.map((opt: any) => opt.value) : [])
                    }
                    options={backupUsers && backupUsers.map((user) => ({ label: user, value: user }))}
                    placeholder={t('select_backup_users')}
                    isMulti
                  />
                  {
                    // <ValidationError {...{ errors, touched, name: "backupUsers" }} />
                  }
                </Form.Group>
              </Col>

              {/* DRIVER EMAIL */}
              <TiassistoElement>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={i18next.t('driver_mail')} required={values.driveItEasy} errors={errors} name='driverEmail' />
                    <Form.Control
                      required={values.driveItEasy}
                      id="driverEmailAnchor"
                      name="driverEmail"
                      type="email"
                      value={values.driverEmail}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </TiassistoElement>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('phone')} required={false} />
                  <Form.Control
                    id="account.phone"
                    name="accountPhone"
                    type="text"
                    disabled={true}
                    value={accountResponse?.Phone}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('email')} required={false} />
                  <Form.Control
                    id="account.email"
                    name="accountEmail"
                    type="text"
                    disabled={true}
                    value={accountResponse?.Email__c}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Submit Button (Uncomment when needed) */}
            {/* <Btn type="submit" icon="arrow-right" className="float-end mt-4" disabled={updating}>
            {t('update')}
          </Btn> */}
          </div>
        </Card.Body>
      </fieldset>
    </Card>
  );
};

export default OpportunityDetails;
